import React, { useState, useEffect } from "react";
import { useFinanceData } from "../hooks/useFinanceData";

const BalancePopup = ({ onClose, visible, deleteItem }) => {
    const { 
        formatAmount,
        updateBankAccountAmount,
        getBalanceItems,
        addBankAccount,
    } = useFinanceData();
    
    const [balances, setBalances] = useState([]);
    
    // Update balances when the popup becomes visible
    useEffect(() => {
        if (visible) {
            setBalances(getBalanceItems() || []);
        }
    }, [visible, getBalanceItems]);
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState("");

    // Start editing a balance
    const handleEditClick = (balance) => {
        setEditingId(balance.id);
        // Convert cents to dollars for editing
        setEditValue((balance.amount).toFixed(2));
    };

    // Save the edited balance
    const handleSaveEdit = async (balance) => {
        try {
            // Parse the edit value as a number
            const newAmount = parseFloat(editValue);
            
            if (isNaN(newAmount)) {
                alert("Please enter a valid number.");
                return;
            }
            
            // Use our specialized function to update the bank account
            await updateBankAccountAmount(balance.id, newAmount);
            setEditingId(null);
            
            // Refresh balances
            setBalances(getBalanceItems());
        } catch (error) {
            console.error("Error updating balance:", error);
            alert("There was an error updating the balance. Please try again.");
        }
    };

    // Cancel editing
    const handleCancelEdit = () => {
        setEditingId(null);
        setEditValue("");
    };

    // Delete a balance
    const handleDeleteBalance = async (balance) => {
        if (window.confirm(`Are you sure you want to delete "${balance.title}" account?`)) {
            try {
                await deleteItem(balance.id);
                // Refresh balances
                setBalances(getBalanceItems());
            } catch (error) {
                console.error("Error deleting balance:", error);
                alert("There was an error deleting the balance. Please try again.");
            }
        }
    };

    // State for new account form
    const [showAddForm, setShowAddForm] = useState(false);
    const [newAccountTitle, setNewAccountTitle] = useState("");
    const [newAccountAmount, setNewAccountAmount] = useState("");
    
    // Handle showing the add account form
    const handleAddAccount = () => {
        setShowAddForm(true);
    };
    
    // Handle canceling the add form
    const handleCancelAdd = () => {
        setShowAddForm(false);
        setNewAccountTitle("");
        setNewAccountAmount("");
    };
    
    // Handle submitting the new account
    const handleSubmitNewAccount = async () => {
        try {
            const amount = parseFloat(newAccountAmount);
            
            if (!newAccountTitle.trim()) {
                alert("Please enter an account title.");
                return;
            }
            
            if (isNaN(amount) || amount <= 0) {
                alert("Please enter a valid positive amount.");
                return;
            }
            
            // Use our addBankAccount function
            await addBankAccount({
                title: newAccountTitle.trim(),
                amount: amount
            });
            
            // Reset form and hide it
            setShowAddForm(false);
            setNewAccountTitle("");
            setNewAccountAmount("");
            
            // Refresh balances
            setBalances(getBalanceItems());
        } catch (error) {
            console.error("Error adding account:", error);
            alert("There was an error adding the account. Please try again.");
        }
    };

    if (!visible) return null;

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Account Balances</h2>
                    <button onClick={onClose} className="close-button">×</button>
                </div>
                
                <div className="modal-body">
                    {balances.length === 0 ? (
                        <div className="no-accounts">
                            <p>No account balances added yet.</p>
                        </div>
                    ) : (
                        <div className="account-list">
                            {balances.map((balance) => (
                                <div key={balance.id} className="account-item">
                                    <div className="account-info">
                                        <div className="account-title">{balance.title}</div>
                                        {editingId === balance.id ? (
                                            <div className="edit-balance-form">
                                                <div className="input-group">
                                                    <span className="dollar-sign">$</span>
                                                    <input
                                                        type="number"
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        step="0.01"
                                                        min="0"
                                                        className="balance-input"
                                                    />
                                                </div>
                                                <div className="edit-actions">
                                                    <button 
                                                        onClick={() => handleSaveEdit(balance)}
                                                        className="save-button"
                                                    >
                                                        Save
                                                    </button>
                                                    <button 
                                                        onClick={handleCancelEdit}
                                                        className="cancel-button"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="account-balance">
                                                <span className="balance-amount">
                                                    {formatAmount(balance.amount)}
                                                </span>
                                                <div className="account-actions">
                                                    <button 
                                                        onClick={() => handleEditClick(balance)}
                                                        className="edit-button"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDeleteBalance(balance)}
                                                        className="delete-button"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="account-last-updated">
                                        Last updated: {new Date(balance.lastUpdated || balance.createdAt).toLocaleString()}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                
                <div className="modal-footer">
                    {showAddForm ? (
                        <div className="add-account-form">
                            <div className="form-group">
                                <label>Account Name</label>
                                <input 
                                    type="text" 
                                    value={newAccountTitle}
                                    onChange={(e) => setNewAccountTitle(e.target.value)}
                                    placeholder="e.g. Checking Account"
                                    className="account-input"
                                />
                            </div>
                            <div className="form-group">
                                <label>Current Balance</label>
                                <div className="input-group">
                                    <span className="dollar-sign">$</span>
                                    <input 
                                        type="number" 
                                        value={newAccountAmount}
                                        onChange={(e) => setNewAccountAmount(e.target.value)}
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                        className="balance-input"
                                    />
                                </div>
                            </div>
                            <div className="form-actions">
                                <button 
                                    onClick={handleSubmitNewAccount}
                                    className="save-button"
                                >
                                    Save
                                </button>
                                <button 
                                    onClick={handleCancelAdd}
                                    className="cancel-button"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button onClick={handleAddAccount} className="add-account-button">
                            Add Account
                        </button>
                    )}
                </div>
            </div>
            
            <style jsx="true">{`
                .modal-backdrop {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.5);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                }
                
                .modal-content {
                    background-color: white;
                    border-radius: 0.5rem;
                    width: 90%;
                    max-width: 500px;
                    max-height: 80vh;
                    overflow-y: auto;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                }
                
                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 1.5rem;
                    border-bottom: 1px solid #e5e7eb;
                }
                
                .modal-header h2 {
                    margin: 0;
                    font-size: 1.25rem;
                }
                
                .close-button {
                    background: none;
                    border: none;
                    font-size: 1.5rem;
                    cursor: pointer;
                    color: #6b7280;
                }
                
                .modal-body {
                    padding: 1.5rem;
                    max-height: calc(80vh - 8rem);
                    overflow-y: auto;
                }
                
                .no-accounts {
                    text-align: center;
                    color: #6b7280;
                    padding: 2rem 0;
                }
                
                .account-list {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }
                
                .account-item {
                    padding: 1rem;
                    border: 1px solid #e5e7eb;
                    border-radius: 0.375rem;
                    background-color: #f9fafb;
                }
                
                .account-info {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }
                
                .account-title {
                    font-weight: bold;
                    font-size: 1rem;
                }
                
                .account-balance {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                
                .balance-amount {
                    font-size: 1.25rem;
                    font-weight: bold;
                }
                
                .account-actions {
                    display: flex;
                    gap: 0.5rem;
                }
                
                .edit-button, .delete-button, .save-button, .cancel-button {
                    padding: 0.25rem 0.5rem;
                    border-radius: 0.25rem;
                    border: none;
                    cursor: pointer;
                    font-size: 0.875rem;
                }
                
                .edit-button {
                    background-color: #e5e7eb;
                    color: #1f2937;
                }
                
                .delete-button {
                    background-color: #fee2e2;
                    color: #991b1b;
                }
                
                .save-button {
                    background-color: #d1fae5;
                    color: #065f46;
                }
                
                .cancel-button {
                    background-color: #e5e7eb;
                    color: #1f2937;
                }
                
                .account-last-updated {
                    font-size: 0.75rem;
                    color: #6b7280;
                    margin-top: 0.5rem;
                }
                
                .edit-balance-form {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }
                
                .input-group {
                    display: flex;
                    align-items: center;
                }
                
                .dollar-sign {
                    margin-right: 0.25rem;
                    font-weight: bold;
                }
                
                .balance-input {
                    padding: 0.375rem 0.5rem;
                    border: 1px solid #d1d5db;
                    border-radius: 0.25rem;
                    width: 100%;
                }
                
                .edit-actions {
                    display: flex;
                    gap: 0.5rem;
                }
                
                .modal-footer {
                    padding: 1rem 1.5rem;
                    border-top: 1px solid #e5e7eb;
                    display: flex;
                    justify-content: flex-end;
                }
                
                .add-account-button {
                    padding: 0.5rem 1rem;
                    background-color: #2563eb;
                    color: white;
                    border: none;
                    border-radius: 0.375rem;
                    cursor: pointer;
                    font-weight: 500;
                }
                
                .add-account-form {
                    width: 100%;
                    padding: 1rem 0;
                }
                
                .form-group {
                    margin-bottom: 1rem;
                }
                
                .form-group label {
                    display: block;
                    margin-bottom: 0.25rem;
                    font-size: 0.875rem;
                    font-weight: 500;
                    color: #374151;
                }
                
                .account-input, .balance-input {
                    width: 100%;
                    padding: 0.5rem;
                    border: 1px solid #d1d5db;
                    border-radius: 0.375rem;
                    font-size: 0.875rem;
                }
                
                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 0.5rem;
                    margin-top: 1rem;
                }
            `}</style>
        </div>
    );
};

export default BalancePopup;